// Key must be in capitalised Snake case to match the error codes returned by the API
export enum ApiErrorCode {
  DUPLICATE_EMAIL,
  DUPLICATE_FIELD,
  UNKNOWN_ERROR,
}

export class ApiError extends Error {
  constructor(
    public readonly code: ApiErrorCode,
    message: string,
    public readonly status: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  private static isKnownErrorCode(code?: string): code is keyof typeof ApiErrorCode {
    return code != null && code in ApiErrorCode;
  }

  static isError(code: ApiErrorCode, error: unknown): error is ApiError {
    if (error instanceof ApiError && ApiErrorCode[error.code] === ApiErrorCode[code]) {
      return true;
    }

    return false;
  }

  static async fromApiResponse(response: Response) {
    const data = await response.json();
    const errorCode = data.detail?.error_code;
    const code = ApiErrorCode[errorCode as keyof typeof ApiErrorCode] ?? ApiErrorCode.UNKNOWN_ERROR;
    const errorMessage = data.detail?.message || 'An error occurred';

    if (this.isKnownErrorCode(errorCode)) {
      if (code === ApiErrorCode.DUPLICATE_FIELD) {
        return new AfternoonDuplicateFieldApiError(
          errorMessage,
          response.status,
          data.detail?.field || 'unknown',
        );
      }
      return new ApiError(ApiErrorCode[errorCode], errorMessage, response.status);
    }

    return new ApiError(code, data.detail?.message || 'An error occurred', response.status);
  }
}

export class AfternoonDuplicateFieldApiError extends ApiError {
  constructor(
    message: string,
    public readonly status: number,
    public readonly field: string,
  ) {
    super(ApiErrorCode.DUPLICATE_FIELD, message, status);
  }

  static isDuplicateFieldError(error: unknown): error is AfternoonDuplicateFieldApiError {
    return (
      this.isError(ApiErrorCode.DUPLICATE_FIELD, error) &&
      error instanceof AfternoonDuplicateFieldApiError
    );
  }
}
